import { Dispatch, SetStateAction, useCallback, useRef, useState } from "react";
import { Properties } from "../rest/responses/Properties";
import { useTeamContext } from "../contexts/TeamContext";
import { Accessor, PropertyAttribute, V3PropertyFilter } from "../types/types";
import { useRestClientContext } from "../contexts/RestClientContext";
import usePropertyQuery from "./usePropertyQuery";
import { PaginationState, SortingState } from "@tanstack/react-table";
import { useDeepCompareEffect } from "use-deep-compare";
import _ from "lodash";
import { isBasePropertyKey } from "../rest/responses/Property";
import { persistentColumns } from "./useDraggableColumns";

const stringIsAccessor = (str: string): str is Accessor => /^\w+\.\w+$/.test(str);

/** Accessors that appear as table columns after syncing server arrangement. Fetch-only prefix fields are dropped
 *  unless they were already in `previouslySelected` (user explicitly added that column). */
function selectedColumnAccessorsFromArrangement(
	arrangementAccessors: Accessor[],
	accessorPrefix: Accessor[],
	previouslySelected: Accessor[]
): Accessor[] {
	const fromServerLayout = arrangementAccessors.filter((a) => !accessorPrefix.includes(a));
	const prefixAccessorsUserChose = previouslySelected.filter((a) => accessorPrefix.includes(a));
	return _.union(fromServerLayout, prefixAccessorsUserChose);
}

function arrangementVisibleInColumnOrder(
	arrangement: string[],
	accessorPrefix: Accessor[],
	selectedColumnAccessors: Accessor[]
): string[] {
	return arrangement.filter((columnId) => {
		if (stringIsAccessor(columnId) && accessorPrefix.includes(columnId)) {
			return selectedColumnAccessors.includes(columnId);
		}
		return true;
	});
}

interface UseTeamPropertiesProps {
	loading?: boolean;
	selectedAccessors: Accessor[];
	selectedPropertyAttributes: PropertyAttribute[];
	pagination: PaginationState | false;
	sorting?: SortingState;
	filters?: V3PropertyFilter[];
	ids?: number[];
	groupId?: number;
	quickSearch?: string;
	setColumnOrder?: Dispatch<SetStateAction<string[]>>;
	setSelectedAccessors?: Dispatch<SetStateAction<Accessor[]>>;
	setSelectedPropertyAttributes?: Dispatch<SetStateAction<PropertyAttribute[]>>;
	accessorPrefix?: Accessor[];
}

export default function useTeamProperties({
	loading = false,
	selectedAccessors,
	selectedPropertyAttributes,
	filters,
	sorting,
	pagination,
	ids,
	groupId,
	quickSearch,
	setColumnOrder,
	setSelectedAccessors,
	setSelectedPropertyAttributes,
	accessorPrefix = [],
}: UseTeamPropertiesProps) {
	const api = useRestClientContext();
	const team = useTeamContext();

	const {
		loading: isLoadingQuery,
		queryId,
		saveArrangement,
		syncAccessors,
	} = usePropertyQuery({
		accessors: selectedAccessors,
		propertyAttributes: selectedPropertyAttributes,
		ids,
		groupId,
	});

	const [isLoadingProperties, setIsLoadingProperties] = useState(true);
	const [isFirstLoad, setIsFirstLoad] = useState(true);
	const [properties, setProperties] = useState<Properties>();
	const [refreshId, setRefreshId] = useState(0);
	const [error, setError] = useState<Error | null>(null);

	const allParams = {
		filters,
		sorting,
		pagination,
		quickSearch,
		refreshId,
		selectedAccessors,
		selectedPropertyAttributes,
		ids,
		groupId,
	};
	const prevFetchParamsRef = useRef(allParams);

	const refresh = useCallback(() => {
		setRefreshId((prev) => prev + 1);
	}, [setRefreshId]);

	useDeepCompareEffect(() => {
		if (loading || isLoadingQuery) return;

		// NB: Don't query "new group"
		if (groupId === -1) return;

		const controller = new AbortController();

		if (!queryId) {
			setError(new Error("Failed to create query. Please try again."));
			return;
		}

		// Only fetch data if this is the first load or if any of the params have changed except for arrangement.
		// This stops superfluous calls on query ID changes or other changes from the backend.
		const fetchParamsChanged = !_.isEqual(prevFetchParamsRef.current, allParams);
		if (!isFirstLoad && !fetchParamsChanged) return;

		setIsLoadingProperties(true);
		setError(null);

		api.v3
			.getPropertiesViaQuery(team, queryId, filters, sorting, pagination, quickSearch, controller.signal)
			.then((response) => {
				prevFetchParamsRef.current = allParams;
				setProperties(response);
				setIsLoadingProperties(false);
				setIsFirstLoad(false);

				if (response["arrangement"]) {
					const arrangement = response["arrangement"];
					const arrangementAccessors = arrangement.filter(stringIsAccessor);
					const arrangementPropertyAttributes = arrangement.filter((str): str is PropertyAttribute =>
						isBasePropertyKey(str)
					);

					if (setSelectedAccessors) {
						// ensure that the accessors selected are not forced into the request (e.g. data integrity)
						setSelectedAccessors((previouslySelected) => {
							const selectedColumnAccessors = selectedColumnAccessorsFromArrangement(
								arrangementAccessors,
								accessorPrefix,
								previouslySelected
							);
							const columnOrderFromServer = arrangementVisibleInColumnOrder(
								arrangement,
								accessorPrefix,
								selectedColumnAccessors
							);
							syncAccessors({
								accessors: _.union(accessorPrefix, selectedColumnAccessors),
								propertyAttributes: arrangementPropertyAttributes,
							});
							setColumnOrder?.([...persistentColumns, ...columnOrderFromServer]);
							setSelectedPropertyAttributes?.(arrangementPropertyAttributes);
							return selectedColumnAccessors;
						});
					} else {
						syncAccessors({
							accessors: _.union(accessorPrefix, arrangementAccessors),
							propertyAttributes: arrangementPropertyAttributes,
						});
						setColumnOrder?.([...persistentColumns, ...arrangement]);
						setSelectedPropertyAttributes?.(arrangementPropertyAttributes);
					}
				}
			})
			.catch((error: Error) => {
				if (error.name === "AbortError") return;
				setError(error);
				setProperties({
					entries: [],
					total_entries: 0,
					page_size: 1,
					page_number: 1,
					total_pages: 1,
				});
				setIsLoadingProperties(false);
			});

		return () => controller.abort("One or more dependencies changed");
	}, [loading, isLoadingQuery, queryId, filters, sorting, pagination, quickSearch, refresh, refreshId]);

	return { loading: isLoadingQuery || isLoadingProperties, properties, refresh, error, queryId, saveArrangement };
}
